
import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  Tooltip,
  MenuItem,
  Select,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';

const AddCertificateIssue = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [certificateName, setCertificateName] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  // Static data for certificates and candidates
  const staticCertificateData = [
    { certificateId: 1, certificateName: 'Certificate of Completion' },
    { certificateId: 2, certificateName: 'Advanced Diploma' },
    { certificateId: 3, certificateName: 'Professional Certification' },
    { certificateId: 4, certificateName: 'Training Certificate' },
    { certificateId: 5, certificateName: 'Workshop Certificate' },
    { certificateId: 6, certificateName: 'Online Course Certificate' },
  ];

  const staticCandidateData = [
    { candidateId: 1, candidateName: 'John Doe' },
    { candidateId: 2, candidateName: 'Jane Smith' },
    { candidateId: 3, candidateName: 'Alice Johnson' },
    { candidateId: 4, candidateName: 'Bob Brown' },
    { candidateId: 5, candidateName: 'Charlie Davis' },
    { candidateId: 6, candidateName: 'Eve Wilson' },
  ];

  useEffect(() => {
    // Auto-fill the issue date with today's date
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setIssueDate(formattedDate);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateName && certificateName && issueDate && expiryDate) {
      setShowSuccessModal(true);
    } else {
      alert('Please fill all fields');
    }
  };

  const handleReset = () => {
    setCandidateName('');
    setCertificateName('');
    setIssueDate('');
    setExpiryDate('');
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Add Certificate Issue
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <div>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="candidate">Candidate Name:</InputLabel>
            <Select
              id="candidate"
              value={candidateName}
              label="Candidate Name"
              onChange={(e) => setCandidateName(e.target.value)}
            >
              {staticCandidateData.map((candidate) => (
                <MenuItem key={candidate.candidateId} value={candidate.candidateName}>
                  {candidate.candidateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="certificate">Certificate Name:</InputLabel>
            <Select
              id="certificate"
              value={certificateName}
              label="Certificate Name"
              onChange={(e) => setCertificateName(e.target.value)}
            >
              {staticCertificateData.map((certificate) => (
                <MenuItem key={certificate.certificateId} value={certificate.certificateName}>
                  {certificate.certificateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Issue Date"
            type="date"
            variant="outlined"
            fullWidth
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                background: theme.palette.mode === 'dark' 
                  ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>')` 
                  : 'inherit',
                width: '16px',
                height: '16px',
                padding: '4px',
                cursor: 'pointer',
                filter: theme.palette.mode === 'dark' ? 'none' : 'inherit',
              },
              '& .MuiInputBase-input[type="date"]': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.default,
                paddingRight: '40px',
              },
            }}
          />
          <TextField
            label="Expiry Date"
            type="date"
            variant="outlined"
            fullWidth
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                background: theme.palette.mode === 'dark' 
                  ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>')` 
                  : 'inherit',
                width: '16px',
                height: '16px',
                padding: '4px',
                cursor: 'pointer',
                filter: theme.palette.mode === 'dark' ? 'none' : 'inherit',
              },
              '& .MuiInputBase-input[type="date"]': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.default,
                paddingRight: '40px',
              },
            }}
          />
          <Tooltip title={!candidateName ? "Select Candidate" : !certificateName ? "Select Certificate" : !issueDate ? "Select Issue Date" : !expiryDate ? "Select Expiry Date" : ""} arrow>
            <span>
              <Button
                variant="contained"
                color="success"
                sx={{ mr: 2 }}
                disabled={!candidateName || !certificateName || !issueDate || !expiryDate}
              >
                Add
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  backgroundColor: '#127498ff',
                  '&:hover': { backgroundColor: '#116280ff' },
                }}
                disabled={!candidateName || !certificateName || !issueDate || !expiryDate}
              >
                SAVE
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleReset}
            sx={{ mr: 2 }}
          >
            RESET
          </Button>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            sx={{
              ml: 2,
              borderColor: theme.palette.secondary.main || '#127498ff',
              color: theme.palette.secondary.main || '#127498ff',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              '&:hover': {
                borderColor: theme.palette.secondary.dark || '#116280ff',
                backgroundColor: theme.palette.secondary.light || 'rgba(18, 116, 152, 0.1)',
              },
              minWidth: '80px',
              padding: '6px 16px',
              visibility: 'visible',
              zIndex: 1,
            }}
          >
            Back
          </Button>
        </div>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Certificate Issue Added ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Certificate Issue Added Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      
    </Container>
  );
};

export default AddCertificateIssue;